import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
    Activity,
    ArrowRight,
    BarChart3,
    BookOpen,
    CalendarDays,
    Check,
    ClipboardList,
    IdCard,
    NotebookPen,
    Route,
    ScanText,
    Send,
    Sparkles,
    TrendingUp,
    Users,
    Wallet,
} from "lucide-react";

export const DetailList = ({ item, f }) => {
    const [cat, setCat] = useState(0);
    return (
        <>
            <p className="mt-8 text-[11px] font-bold uppercase tracking-[0.24em] text-[#0D9488]">{f.howItWorks}</p>
            <ul className="mt-4 space-y-3.5">
                {item.details.map((d, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-[#475569]">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#0D9488]/40 bg-[#0D9488]/10">
                            <Check size={11} className="text-[#0D9488]" />
                        </span>
                        {d}
                    </li>
                ))}
            </ul>
            {item.num === "08" && (
                <div className="mt-9" data-testid="note-categories">
                    <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#0D9488]">{f.catsLabel}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {f.noteCategories.map((c, i) => (
                            <button
                                key={c.name}
                                onClick={() => setCat(i)}
                                aria-pressed={cat === i}
                                className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] ${
                                    cat === i
                                        ? "border-[#0D9488] bg-[#0D9488]/10 text-[#0F172A]"
                                        : "border-[#E2E8F0] bg-white text-[#475569] hover:border-[#0D9488]/60 hover:text-[#0F172A]"
                                }`}
                                data-testid={`note-cat-${i}`}
                            >
                                {c.name}
                            </button>
                        ))}
                    </div>
                    <AnimatePresence mode="wait">
                        <motion.p
                            key={cat}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.25 }}
                            className="mt-4 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-5 py-4 text-sm leading-relaxed text-[#475569]"
                            data-testid="note-cat-desc"
                        >
                            {f.noteCategories[cat].desc}
                        </motion.p>
                    </AnimatePresence>
                </div>
            )}
        </>
    );
};

const CARD_ICONS = [Users, CalendarDays, NotebookPen, Wallet, ClipboardList, BookOpen, IdCard, Sparkles];
const AI_ICONS = [ScanText, Send, BarChart3, TrendingUp, Activity, Route];

const reveal = {
    hidden: { opacity: 0, y: 28 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export const FeaturesSection = ({ t }) => {
    const o = t.features.overview;
    const ai = t.features.aiStrip;

    return (
        <section id="features" className="scroll-mt-24 bg-[#F8FAFC]" data-testid="features-section">
            {/* Feature overview cards */}
            <div className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-32">
                <motion.div
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ staggerChildren: 0.1 }}
                    className="mb-14 max-w-2xl"
                >
                    <motion.p variants={reveal} className="text-xs font-bold uppercase tracking-[0.26em] text-[#0D9488] md:text-sm" data-testid="features-overline">
                        {o.overline}
                    </motion.p>
                    <motion.h2 variants={reveal} className="font-display mt-4 text-4xl leading-tight tracking-tight text-[#0F172A] md:text-5xl" data-testid="features-title">
                        {o.title}
                    </motion.h2>
                    <motion.p variants={reveal} className="mt-5 text-base leading-relaxed text-[#475569] md:text-lg">
                        {o.text}
                    </motion.p>
                    <motion.a
                        variants={reveal}
                        href="/features"
                        className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#0D9488] transition-colors duration-300 hover:text-[#0E4B57]"
                        data-testid="features-cta-button"
                    >
                        {o.cta}
                        <ArrowRight size={16} />
                    </motion.a>
                </motion.div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="features-cards">
                    {o.cards.map((c, i) => {
                        const Icon = CARD_ICONS[i % CARD_ICONS.length];
                        return (
                            <motion.div
                                key={c.title}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.6, delay: (i % 4) * 0.07, ease: [0.22, 1, 0.36, 1] }}
                                className="group rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#0D9488]/40 hover:shadow-md"
                                data-testid={`feature-card-${i}`}
                            >
                                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0D9488]/10 text-[#0D9488] transition-colors duration-300 group-hover:bg-[#0D9488] group-hover:text-white">
                                    <Icon size={20} />
                                </div>
                                <h3 className="mt-5 text-base font-bold text-[#0F172A]">{c.title}</h3>
                                <p className="mt-1.5 text-sm leading-relaxed text-[#64748B]">{c.desc}</p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            {/* AI capabilities strip */}
            <div className="border-y border-[#E2E8F0] bg-[#F1F5F9]" data-testid="ai-strip">
                <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-24">
                    <motion.div
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ staggerChildren: 0.1 }}
                        className="mb-12 max-w-2xl"
                    >
                        <motion.p variants={reveal} className="text-xs font-bold uppercase tracking-[0.26em] text-[#0D9488] md:text-sm" data-testid="ai-strip-overline">
                            {ai.overline}
                        </motion.p>
                        <motion.h2 variants={reveal} className="font-display mt-4 text-3xl leading-tight tracking-tight text-[#0F172A] md:text-4xl" data-testid="ai-strip-title">
                            {ai.title}
                        </motion.h2>
                        <motion.p variants={reveal} className="mt-4 text-base leading-relaxed text-[#475569]">
                            {ai.text}
                        </motion.p>
                        <motion.a
                            variants={reveal}
                            href="/features"
                            className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#0D9488] transition-colors duration-300 hover:text-[#0E4B57]"
                            data-testid="ai-strip-link"
                        >
                            {ai.link}
                            <ArrowRight size={16} />
                        </motion.a>
                    </motion.div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="ai-strip-cards">
                        {ai.items.map((item, i) => {
                            const Icon = AI_ICONS[i % AI_ICONS.length];
                            return (
                                <motion.div
                                    key={item.title}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.55, delay: (i % 3) * 0.07, ease: [0.22, 1, 0.36, 1] }}
                                    className="flex items-start gap-4 rounded-2xl border border-[#E2E8F0] bg-white p-5 transition-colors duration-300 hover:border-[#D97706]/40"
                                    data-testid={`ai-card-${i}`}
                                >
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#D97706]/10 text-[#D97706]">
                                        <Icon size={18} />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold text-[#0F172A]">{item.title}</h3>
                                        <p className="mt-1 text-xs leading-relaxed text-[#64748B]">{item.desc}</p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};
